/**
 * @catworld/engine -- simulation + level geometry, whole, at the root.
 *
 * This is the only door.  The package's exports map declares "." and
 * nothing else, so a deep import (`@catworld/engine/src/player.js`) is a
 * resolution error rather than a convention -- docs/ARCH-PLAN.md §1.1's
 * root-imports-only rule, enforced where it can actually refuse.
 *
 * The boundary this file fronts (§1.1's `engine` row): everything here
 * imports cleanly under plain Node.  Importing the package builds no scene
 * and touches no DOM -- textures.js returns null where there is no
 * `document` to draw on, and Builder.finish() is the one scene-touching
 * step, called only by buildLevel(scene), which is the client's wrapper
 * and simply never called headless.  scripts/gates/engine-import.mjs holds
 * that promise where it can go red.
 *
 * One name needs a word: SPAWN is declared in level/spec.js and re-exported
 * by level/index.js (beside the build that plants its marker), and ES star
 * exports silently EXCLUDE any name two modules offer from DIFFERENT
 * declarations.  These two resolve to the same binding, so the root carries
 * it -- but the explicit line at the bottom pins that to the declaration,
 * the same defence shared's index gives PROTO_VERSION.
 *
 * MATS / useEnvironment / tickWater are exported by name, not by star: they
 * are the level's material palette and its day/water hooks, and their one
 * outside consumer is the client's day cycle (daycycle.js tints the street
 * as the sun moves).  Textures stay internal -- nothing outside materials.js
 * has ever asked for one.
 */

/**
 * The input struct -- the contract three packages fill and one file reads.
 *
 * player.js's update(dt, input, camYaw) reads exactly these ten keys and no
 * others.  The browser fills them from keys and the touch pad (app/context.js,
 * touch.js), the server's brain fills a fresh object per cat (simcat.js's
 * freshInput -- shared input objects are how one brain steers sixteen cats),
 * and a gamepad will fill them the same way.  Until WS-06 this shape was
 * held only by comments (docs/ARCH-PLAN.md C6); it is now written down.
 *
 * The contract is TOTAL, like the wire parsers: every field is optional,
 * and player.js's axis() resolves a key's 1, a stick's 0.42, an undefined
 * field on a harness-built object and a NaN from arithmetic on one, all to
 * a number it can multiply by.  A missing field means released.
 *
 * The four direction fields are MAGNITUDES in [0, 1], not flags -- half a
 * stick is half the speed of the selected gait, which is what lets a cat
 * creep (a keyboard writes exactly 0 or 1, so nothing about key play
 * changes).  The rest are levels, read for truthiness.  Two need care:
 * `jump` is a LEVEL, not an edge -- player.js charges while it is high and
 * launches when it falls, so a writer must hold it down and let it go
 * (main's kb latch and touch.js both exist to get this right).  `sitToggle`
 * is a level too, but its writers pulse it for one frame (context.js sets
 * it on keydown, the loop zeroes it; the brain sets it for one tick), since
 * a held toggle would flip the cat every frame.
 *
 * @typedef {object} PlayerInput
 * @property {number | boolean} [forward]   magnitude toward the camera's far side
 * @property {number | boolean} [back]      magnitude toward the camera
 * @property {number | boolean} [left]      magnitude screen-left
 * @property {number | boolean} [right]     magnitude screen-right
 * @property {number | boolean} [sprint]    level: run gait while held
 * @property {number | boolean} [jump]      LEVEL: high gathers charge, the
 *                                          falling edge launches; a tangled
 *                                          cat spends fresh presses as wriggles
 * @property {number | boolean} [sitToggle] pulsed for one frame by its writers
 * @property {number | boolean} [stalk]     level: creep gait while held
 * @property {number | boolean} [swipe]     level: starts the one-shot swipe
 * @property {number | boolean} [claw]      level: claws out; airborne against
 *                                          a climbable face they hook and cling
 */

/**
 * Who is driving a server-side cat -- SimCat's discriminant, written down
 * (docs/ARCH-PLAN.md C6; server/src/simcat.js holds the prose original).
 *
 * The one rule: `driver` is the discriminant.  Non-null means a human
 * socket is at the wheel and `brain` is null; null means the brain drives.
 * Nothing else may be used to tell the two apart, because the two lists a
 * reader could otherwise drift between (has a brain / lacks a driver) go
 * wrong in opposite directions during the one frame of a handover.  The
 * engine deliberately does not know what a driver IS -- a ws socket today,
 * anything with a lifetime tomorrow -- only that its presence is the fact.
 *
 * @typedef {{ driver: object, brain: null }} HumanDriven
 * @typedef {{ driver: null, brain: object }} BrainDriven
 * @typedef {HumanDriven | BrainDriven} CatSeat
 */

export * from "./player.js";
export * from "./collision.js";
export * from "./burrs.js";
export * from "./pounce.js";
export * from "./level/spec.js";
export * from "./level/hero/spec.js";
export * from "./level/navgrid.js";
export * from "./level/builder.js";
export * from "./level/headless.js";
export * from "./level/index.js";
export { MATS, useEnvironment, tickWater } from "./level/materials.js";
export { SPAWN } from "./level/spec.js";
